import { useCallback, useEffect, useRef } from 'react';
import { useGlobalStore } from './use-store';
import { VevSystemVariables } from '@vev/interactions';

export function useSystemVariableSubscription() {
  const stateCbs = useRef<Array<(systemVariables: VevSystemVariables) => void>>([]);

  const mouseData = useRef({
    x: 0,
    y: 0,
    xPercent: 0,
    yPercent: 0,
    dx: 0,
    dy: 0,
  });
  const prevMousePos = useRef({ x: 0, y: 0, time: 0 });
  const rafId = useRef<number | null>(null);
  const pendingMouseUpdate = useRef(false);
  const storeState = useRef({
    width: 0,
    height: 0,
    device: '',
    scrollPixelsY: 0,
    scrollPercentageY: 0,
  });

  // Subscribe to store changes and emit with current mouse data
  useGlobalStore((s) => {
    storeState.current = {
      width: s.viewport.width,
      height: s.viewport.height,
      device: s.device,
      scrollPixelsY: s.scrollTop,
      scrollPercentageY: Math.trunc(
        ((s.scrollTop + s.viewport.height) / s.viewport.scrollHeight) * 100,
      ),
    };

    stateCbs.current.forEach((cb) => {
      cb({
        'vev/width': storeState.current.width,
        'vev/height': storeState.current.height,
        'vev/device': storeState.current.device,
        'vev/scrollPixelsY': storeState.current.scrollPixelsY,
        'vev/scrollPercentageY': storeState.current.scrollPercentageY,
        'vev/mouseX': mouseData.current.x,
        'vev/mouseY': mouseData.current.y,
        'vev/mouseXPercent': mouseData.current.xPercent,
        'vev/mouseYPercent': mouseData.current.yPercent,
        'vev/mouseDX': mouseData.current.dx,
        'vev/mouseDY': mouseData.current.dy,
      });
    });
  });

  // Mouse tracking isolated to this hook - only subscribers get updates
  useEffect(() => {
    const updateMouseData = () => {
      if (!pendingMouseUpdate.current) return;

      // Emit mouse changes with current store state
      stateCbs.current.forEach((cb) => {
        cb({
          'vev/width': storeState.current.width,
          'vev/height': storeState.current.height,
          'vev/device': storeState.current.device,
          'vev/scrollPixelsY': storeState.current.scrollPixelsY,
          'vev/scrollPercentageY': storeState.current.scrollPercentageY,
          'vev/mouseX': mouseData.current.x,
          'vev/mouseY': mouseData.current.y,
          'vev/mouseXPercent': mouseData.current.xPercent,
          'vev/mouseYPercent': mouseData.current.yPercent,
          'vev/mouseDX': mouseData.current.dx,
          'vev/mouseDY': mouseData.current.dy,
        });
      });

      pendingMouseUpdate.current = false;
      rafId.current = null;
    };

    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      const currentTime = performance.now();
      const deltaTime = currentTime - prevMousePos.current.time;

      let clientX: number;
      let clientY: number;

      if (e instanceof MouseEvent) {
        ({ clientX, clientY } = e);
      } else {
        const touch = e.touches[0] || e.changedTouches[0];
        if (!touch) return;
        ({ clientX, clientY } = touch);
      }

      // Calculate delta (velocity)
      const dx = deltaTime > 0 ? (clientX - prevMousePos.current.x) / deltaTime : 0;
      const dy = deltaTime > 0 ? (clientY - prevMousePos.current.y) / deltaTime : 0;

      prevMousePos.current = { x: clientX, y: clientY, time: currentTime };

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      mouseData.current = {
        x: clientX,
        y: clientY,
        xPercent: viewportWidth > 0 ? (clientX / viewportWidth) * 100 : 0,
        yPercent: viewportHeight > 0 ? (clientY / viewportHeight) * 100 : 0,
        dx: Math.round(dx * 1000) / 1000,
        dy: Math.round(dy * 1000) / 1000,
      };

      pendingMouseUpdate.current = true;

      // Throttle with requestAnimationFrame
      if (rafId.current === null) {
        rafId.current = requestAnimationFrame(updateMouseData);
      }
    };

    prevMousePos.current = { x: 0, y: 0, time: performance.now() };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('touchmove', handleMouseMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleMouseMove);
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, []);

  return useCallback((cb: (systemVariables: VevSystemVariables) => void) => {
    stateCbs.current = [...stateCbs.current, cb];

    return () => {
      stateCbs.current = stateCbs.current.filter((existingCb) => existingCb !== cb);
    };
  }, []);
}
